/**
 * Writing a "show this only when…" rule.
 *
 * One level of grouping - every test, or any test - because that covers what
 * an application form actually needs and stays readable by the person who has
 * to maintain it a year from now. The field list offers only what this product
 * asks, since a rule reading anything else could never come true; the server
 * refuses those too, so the two agree.
 */
import type { Condition, ComparisonOperator, DataType } from "@dp/shared";
import { NO_VALUE_OPERATORS } from "@dp/shared";

export interface ConditionField {
  key: string;
  label: string;
  dataType: DataType;
  options?: { code: string; label: string }[];
}

interface Row {
  field: string;
  op: ComparisonOperator;
  value: unknown;
}

const OPERATORS: Record<string, { op: ComparisonOperator; label: string }[]> = {
  default: [
    { op: "eq", label: "is" },
    { op: "ne", label: "is not" },
    { op: "is_present", label: "is answered" },
    { op: "is_blank", label: "is not answered" },
  ],
  numeric: [
    { op: "eq", label: "is" },
    { op: "ne", label: "is not" },
    { op: "gt", label: "is more than" },
    { op: "gte", label: "is at least" },
    { op: "lt", label: "is less than" },
    { op: "lte", label: "is at most" },
    { op: "is_present", label: "is answered" },
    { op: "is_blank", label: "is not answered" },
  ],
  choice: [
    { op: "eq", label: "is" },
    { op: "ne", label: "is not" },
    { op: "in", label: "is one of" },
    { op: "not_in", label: "is none of" },
    { op: "is_present", label: "is answered" },
    { op: "is_blank", label: "is not answered" },
  ],
  text: [
    { op: "eq", label: "is" },
    { op: "ne", label: "is not" },
    { op: "contains", label: "contains" },
    { op: "is_present", label: "is answered" },
    { op: "is_blank", label: "is not answered" },
  ],
};

/** A value the control for this field can actually represent, used whenever
 *  the rule is pointed at a different field. */
function blankValueFor(field: ConditionField | undefined): unknown {
  if (field?.dataType === "multi_choice") return [];
  return "";
}

function operatorsFor(dataType: DataType | undefined) {
  if (!dataType) return OPERATORS.default!;
  if (["integer", "decimal", "money", "percent", "date"].includes(dataType)) return OPERATORS.numeric!;
  if (["choice", "multi_choice"].includes(dataType)) return OPERATORS.choice!;
  if (["text", "long_text", "email", "phone"].includes(dataType)) return OPERATORS.text!;
  return OPERATORS.default!;
}

/** The stored shape, flattened into rows the editor can draw. Anything more
 *  complicated than one level is shown read-only rather than mangled. */
function toRows(condition: Condition | null): { mode: "all" | "any"; rows: Row[]; simple: boolean } {
  if (!condition) return { mode: "all", rows: [], simple: true };

  const unwrap = (nodes: Condition[]): { rows: Row[]; simple: boolean } => {
    const rows: Row[] = [];
    for (const node of nodes) {
      if ("field" in node) rows.push({ field: node.field, op: node.op, value: node.value });
      else return { rows: [], simple: false };
    }
    return { rows, simple: true };
  };

  if ("all" in condition) return { mode: "all", ...unwrap(condition.all) };
  if ("any" in condition) return { mode: "any", ...unwrap(condition.any) };
  if ("field" in condition) return { mode: "all", rows: [{ field: condition.field, op: condition.op, value: condition.value }], simple: true };
  return { mode: "all", rows: [], simple: false };
}

function toCondition(mode: "all" | "any", rows: Row[]): Condition | null {
  const usable = rows.filter((row) => row.field && row.op);
  if (!usable.length) return null;
  const parts: Condition[] = usable.map((row) =>
    NO_VALUE_OPERATORS.includes(row.op) ? { field: row.field, op: row.op } : { field: row.field, op: row.op, value: row.value },
  );
  return mode === "all" ? { all: parts } : { any: parts };
}

export function ConditionEditor({
  value, fields, onChange,
}: {
  value: Condition | null;
  fields: ConditionField[];
  onChange: (condition: Condition | null) => void;
}) {
  const { mode, rows, simple } = toRows(value);

  if (!simple) {
    return (
      <div className="notice notice-info small">
        This rule was written with nested groups, which this editor does not draw. It still works — clear it to start again.
        <div><button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => onChange(null)}>Clear the rule</button></div>
      </div>
    );
  }

  function update(nextMode: "all" | "any", nextRows: Row[]) {
    onChange(toCondition(nextMode, nextRows));
  }

  if (rows.length === 0) {
    return (
      <div className="row">
        <span className="small muted">Always shown.</span>
        <button
          type="button" className="btn btn-sm" disabled={fields.length === 0}
          onClick={() => update(mode, [{ field: fields[0]?.key ?? "", op: "eq", value: "" }])}
        >
          Only show it when…
        </button>
        {fields.length === 0 && <span className="small muted">Attach another field first — a rule needs something to read.</span>}
      </div>
    );
  }

  return (
    <div className="condition">
      <div className="row small">
        <span className="muted">Show it when</span>
        <select className="control control-inline" value={mode} onChange={(event) => update(event.target.value as "all" | "any", rows)}>
          <option value="all">every one of these is true</option>
          <option value="any">any one of these is true</option>
        </select>
      </div>

      {rows.map((row, index) => {
        const field = fields.find((candidate) => candidate.key === row.field);
        const operators = operatorsFor(field?.dataType);
        const needsValue = !NO_VALUE_OPERATORS.includes(row.op);

        return (
          <div className="condition-row" key={index}>
            <select
              className="control" value={row.field}
              onChange={(event) => {
                const chosen = fields.find((candidate) => candidate.key === event.target.value);
                const next = [...rows];
                next[index] = { ...row, field: event.target.value, value: blankValueFor(chosen) };
                update(mode, next);
              }}
            >
              {fields.map((candidate) => (
                <option key={candidate.key} value={candidate.key}>{candidate.label}</option>
              ))}
            </select>

            <select
              className="control" value={row.op}
              onChange={(event) => {
                const next = [...rows];
                next[index] = { ...row, op: event.target.value as ComparisonOperator };
                update(mode, next);
              }}
            >
              {operators.map((operator) => (
                <option key={operator.op} value={operator.op}>{operator.label}</option>
              ))}
            </select>

            {needsValue ? <ValueInput field={field} row={row} onChange={(value) => {
              const next = [...rows];
              next[index] = { ...row, value };
              update(mode, next);
            }} /> : <span />}

            <button
              type="button" className="btn btn-sm btn-ghost" aria-label="Remove this test"
              onClick={() => update(mode, rows.filter((_, position) => position !== index))}
            >
              ×
            </button>
          </div>
        );
      })}

      <button
        type="button" className="btn btn-sm"
        onClick={() => update(mode, [...rows, { field: fields[0]?.key ?? "", op: "eq", value: "" }])}
      >
        Add a test
      </button>
    </div>
  );
}

function ValueInput({ field, row, onChange }: { field: ConditionField | undefined; row: Row; onChange: (value: unknown) => void }) {
  if (field?.dataType === "boolean") {
    // Rendered strictly from what is stored. Showing "Yes" while the rule
    // actually held an empty string produced a condition that silently never
    // matched, and the editor looked right the whole time.
    return (
      <select
        className="control"
        value={row.value === true ? "true" : row.value === false ? "false" : ""}
        onChange={(event) => onChange(event.target.value === "" ? "" : event.target.value === "true")}
      >
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (field?.options?.length) {
    if (row.op === "in" || row.op === "not_in") {
      const selected = new Set(Array.isArray(row.value) ? (row.value as string[]) : []);
      return (
        <div className="chips">
          {field.options.map((option) => (
            <label key={option.code} className={`chip picky${selected.has(option.code) ? " chip-accent" : ""}`}>
              <input
                type="checkbox" checked={selected.has(option.code)}
                onChange={(event) => {
                  const next = new Set(selected);
                  if (event.target.checked) next.add(option.code);
                  else next.delete(option.code);
                  onChange([...next]);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      );
    }
    return (
      <select className="control" value={String(row.value ?? "")} onChange={(event) => onChange(event.target.value)}>
        <option value="">Choose…</option>
        {field.options.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
      </select>
    );
  }

  const numeric = field && ["integer", "decimal", "money", "percent"].includes(field.dataType);
  return (
    <input
      className="control"
      type={numeric ? "number" : field?.dataType === "date" ? "date" : "text"}
      value={String(row.value ?? "")}
      onChange={(event) => onChange(numeric && event.target.value !== "" ? Number(event.target.value) : event.target.value)}
    />
  );
}
